import { type Message, useStores } from '@littlebird/sdk';
import {
  getTodoWriteSummary,
  opensTurn,
  type ReasoningTodoItemLike,
} from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import { cn } from '@/lib/utils';

import { todoPlanDismissStore } from './todo-plan-dismiss.store';

const TODO_WRITE = 'todo_write';

type CurrentTurnSnapshot = {
  turnStartMessageId: string | null;
  todoItems: ReasoningTodoItemLike[] | null;
};

// Single backward pass: collect the latest TodoWrite snapshot since the user
// message that opened the current turn AND that message's id. Bounded by
// O(latest-turn-size), not O(thread-size), because it stops at that message.
// A message the user sent into the running turn does not end the scan.
//
// Intentionally not memoized: observer-wrapped components track which fields
// of `messages[i]` were read here (role, joinedTurn, modalitiesUsed). Mutations to those
// fields invalidate the render at the right cadence; `messages[i].content`
// streaming doesn't trigger this re-run. `useMemo([messages])` was tried and
// reverted — MobX often keeps the array reference stable while mutating
// nested observables in place, which left the panel showing stale TodoWrite
// payloads mid-stream.
const getCurrentTurnSnapshot = (messages: Message[]): CurrentTurnSnapshot => {
  let todoItems: ReasoningTodoItemLike[] | null = null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (opensTurn(message)) {
      return { turnStartMessageId: message.id, todoItems };
    }
    if (todoItems) continue;
    const modalities = message.modalitiesUsed ?? [];
    for (let j = modalities.length - 1; j >= 0; j--) {
      const modality = modalities[j];
      if (modality.type === TODO_WRITE) {
        todoItems =
          (modality.todos as ReasoningTodoItemLike[] | undefined) ?? [];
        break;
      }
    }
  }
  return { turnStartMessageId: null, todoItems };
};

const TaskStatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'completed':
      return <Icons.squareCheck className="size-3.5 text-success-9" />;
    case 'in_progress':
      return (
        <Icons.square className="size-3.5 text-[var(--brand-jordy-blue)] animate-pulse" />
      );
    case 'cancelled':
      return <Icons.squareX className="size-3.5 text-danger-10" />;
    default:
      return <Icons.square className="size-3.5 text-foreground-muted" />;
  }
};

const TaskRow = ({ item }: { item: ReasoningTodoItemLike }) => {
  const isCompleted = item.status === 'completed';
  const isCancelled = item.status === 'cancelled';
  const isPending = item.status === 'pending';

  return (
    <XStack className="items-center gap-2 text-sm">
      <TaskStatusIcon status={item.status} />
      <span
        className={cn(
          'min-w-0 truncate',
          isCancelled && 'text-foreground-subtle line-through',
          isCompleted && 'text-foreground-subtle',
          isPending && 'text-foreground-muted',
          !isCancelled && !isCompleted && !isPending && 'text-foreground'
        )}
      >
        {item.content}
      </span>
    </XStack>
  );
};

export const TodoPlanPanel = observer(({ threadId }: { threadId: string }) => {
  const { messages: messagesStore } = useStores();
  const messages = messagesStore.getMessagesByThreadId(threadId);
  const [isExpanded, setIsExpanded] = useState(false);

  const { turnStartMessageId, todoItems: items } =
    getCurrentTurnSnapshot(messages);

  if (!items || items.length === 0) return null;
  if (todoPlanDismissStore.isDismissed(threadId, turnStartMessageId))
    return null;

  const { doneCount, totalCount, headerItem } = getTodoWriteSummary(items);
  const allDone = doneCount === totalCount;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      transition={{ duration: 0.18 }}
      className="mb-2 overflow-hidden rounded-xl border border-border-strong bg-surface"
    >
      <XStack className="items-stretch">
        <button
          type="button"
          className="flex flex-1 min-w-0 items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-surface-hover"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
        >
          <Icons.list className="size-3.5 flex-shrink-0 text-foreground-muted" />
          <span className="text-sm font-medium text-foreground flex-shrink-0">
            {allDone ? 'Plan complete' : 'Plan'}
          </span>
          <span className="text-xs text-foreground-muted flex-shrink-0">
            {doneCount}/{totalCount}
          </span>
          {!isExpanded && !allDone && headerItem && (
            <>
              <span className="text-foreground-subtle flex-shrink-0">·</span>
              <span className="text-xs text-foreground-muted truncate">
                {headerItem.content}
              </span>
            </>
          )}
          <Icons.chevronDown
            className={cn(
              'size-3.5 flex-shrink-0 text-foreground-muted transition-transform ml-auto',
              isExpanded && 'rotate-180'
            )}
          />
        </button>
        <button
          type="button"
          className="flex flex-shrink-0 items-center px-2 text-foreground-muted transition-colors hover:bg-surface-active hover:text-foreground"
          onClick={() =>
            todoPlanDismissStore.dismiss(threadId, turnStartMessageId)
          }
          aria-label="Dismiss plan"
        >
          <Icons.cancel className="size-3.5" />
        </button>
      </XStack>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="plan-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <YStack className="gap-1.5 border-t border-border px-3 py-2">
              {items.map((item) => (
                <TaskRow key={item.id} item={item} />
              ))}
            </YStack>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});
